import {
  GetterTree, Module, MutationTree, ActionTree, ActionContext,
} from 'vuex'
import { Message } from '@/ts/interfaces/message'
import { RootState } from '..'
import { RestChat } from '@/rest/chat'
import { EventBus } from '@/event-bus'
import { RestUser } from '@/rest/user'
import { RestFile } from '@/rest/file'
import { Poll } from '@/ts/interfaces/poll'
import { Popup } from '@/ts/interfaces/popup'
import { ON_MESSAGES, ON_POPUP } from '@/constants/events'
import { ScheduledMessage } from '@/ts/interfaces/scheduled-message'
import { Quiz, QuizUserScores } from '@/ts/interfaces/quiz'
import { QuizAnswer } from '@/ts/interfaces/quiz-answer'

const restChat = new RestChat()
const restUser = new RestUser()
const restFile = new RestFile()

const CHAT_MESSAGE_LIMIT = 80

type MessageArrayRo = readonly (Message | Poll | Quiz)[]

export interface ChatState {
  messages: MessageArrayRo
  artistMessages: MessageArrayRo
  messageIds: string[]
  artistMessageIds: string[]
  pollIds: { [key: string]: string }
  quizIds: { [key: string]: string }
  popup: Popup | null,
  replyId: string
  artistFilter: boolean
  scheduledMessages: ScheduledMessage[]
  polls: Poll[]
  quizzes: Quiz[]
  messagesShownCount: number
  messageIndexLocked: number
  userScoresById: { [key: number]: number } // User quiz scores {user_id: score}
  totalQuizQuestions: number
}

const chatState: ChatState = {
  messages: [],
  messageIds: [],
  artistMessages: [],
  artistMessageIds: [],
  pollIds: {},
  quizIds: {},
  popup: null,
  replyId: '',
  artistFilter: false,
  scheduledMessages: [],
  polls: [],
  quizzes: [],
  messagesShownCount: CHAT_MESSAGE_LIMIT,
  messageIndexLocked: -1,
  userScoresById: {},
  totalQuizQuestions: 0
}

function isArtistMessage(
  message: Message | Poll | Quiz, currentUserId: number
): boolean {
  return (
    message.sender.role === 'admin' ||
    message.sender.role === 'host' ||
    currentUserId === message.sender.id
  )
}

const mutations: MutationTree<ChatState> = {
  SET_CHAT_MESSAGES(state: ChatState, p: { messages: (Message | Poll | Quiz)[], currentUserId: number }) {
    const artistMessages = p.messages.filter(m => isArtistMessage(m, p.currentUserId))
    state.messages = Object.freeze(p.messages)
    state.artistMessages = Object.freeze(artistMessages)
    state.messageIds = p.messages.map(m => m.id)
    state.artistMessageIds = artistMessages.map(m => m.id)
    for (const message of p.messages) {
      state.userScoresById[message.sender.id] = message.sender.quiz_score
    }
  },
  ADD_CHAT_MESSAGE(state: ChatState, p: { message: Message, currentUserId: number }) {
    const message = p.message
    state.messages = Object.freeze(state.messages.concat([message]))
    state.messageIds = state.messageIds.slice().concat([message.id])
    // Add all artist and current user message to separate array
    if (isArtistMessage(message, p.currentUserId)) {
      state.artistMessages = Object.freeze(state.artistMessages.concat([message]))
      state.artistMessageIds = state.artistMessageIds.concat([message.id])
    }
    state.userScoresById[p.message.sender.id] = p.message.sender.quiz_score
  },
  UPDATE_USER_SCORES(state: ChatState, message: QuizUserScores) {
    const userIds = message.user_ids
    const scores = message.scores
    for (let i = 0; i < userIds.length; i++) {
      state.userScoresById[userIds[i]] = scores[i]
    }
    state.totalQuizQuestions = message.total_questions
  },
  SET_CHAT_POPUP(state: ChatState, popup) {
    state.popup = popup
  },
  SET_POLL_ID(state: ChatState, p: { pollId: string, messageId: string }) {
    state.pollIds[p.pollId] = p.messageId
  },
  SET_QUIZ_ID(state: ChatState, p: { quizId: string, messageId: string }) {
    state.quizIds[p.quizId] = p.messageId
  },
  DELETE_CHAT_MESSAGE(state: ChatState, chatId) {
    const index = state.messageIds.indexOf(chatId)

    if (index <= -1) {
      return
    }

    state.messages = state.messages.filter((_, i) => i !== index)
    state.messageIds = state.messageIds.filter((_, i) => i !== index)

    const artistIndex = state.artistMessageIds.indexOf(chatId)
    if (index <= -1) {
      return
    }
    state.artistMessages = state.artistMessages.filter((_, i) => i !== artistIndex)
    state.artistMessageIds = state.artistMessageIds.filter((_, i) => i !== artistIndex)
  },
  SET_CHAT_REPLY_ID(state: ChatState, replyId) {
    state.replyId = replyId
  },
  SET_CHAT_ARTIST_FILTER(state: ChatState, artistFilter) {
    state.artistFilter = artistFilter
  },
  SET_CHAT_MESSAGES_SHOWN_COUNT(state: ChatState, value: number) {
    state.messagesShownCount = value
  },
  SET_CHAT_SCHEDULED_MESSAGES(state: ChatState, scheduledMessages) {
    state.scheduledMessages = scheduledMessages
  },
  DELETE_CHAT_SCHEDULED_MESSAGE(state: ChatState, scheduledMessageId) {
    const index = state.scheduledMessages.findIndex((m) => m.id === scheduledMessageId)

    if (index >= 0) {
      state.scheduledMessages.splice(index, 1)
    }
  },
  ADD_CHAT_POLL(state: ChatState, poll: Poll) {
    if (state.polls.find((p) => !p.closed && p.poll_id === poll.poll_id)) return

    state.polls.push(poll)
  },
  ADD_CHAT_QUIZ(state: ChatState, quiz: Quiz) {
    if (state.quizzes.find((p) => p.question_id === quiz.question_id)) return

    state.quizzes.push(quiz)
  },
  SET_CHAT_MESSAGE_ID_LOCKED(state: ChatState, messageId: string) {
    state.messageIndexLocked = state.messageIds.indexOf(messageId)
  },
}

const actions: ActionTree<ChatState, RootState> = {
  async CHAT_LOAD_MESSAGES(ctx: ActionContext<ChatState, RootState>): Promise<boolean> {
    // Load more old messages, used when scrolling further up in the chat
    const lastTimestamp = ctx.state.messages[0]?.timestamp_ms || null
    let before: string | null = null
    if (lastTimestamp) {
      // Set ?before param to timestamp of last message
      // rounded up to nearest 30 seconds
      // to improve odds of cache hit
      const beforeDate = new Date(lastTimestamp)
      beforeDate.setSeconds(Math.ceil(beforeDate.getSeconds() / 30) * 30)
      beforeDate.setMilliseconds(0)
      before = beforeDate.toISOString()
    }

    if (before) {
      // Overfetch 200 messages because the rounded timestamp
      // can cause the results to include messages we already have loaded
      const { items } = await restChat.get(ctx.getters.partyId, before, 200)

      if (items.length === 0) {
        return false
      }
      // Create new message arrays with the old + current messages
      // deduplicated. Needed because some artist messages here might be already loaded
      const oldIds = ctx.state.messageIds
      let filtered = items.filter(m => !oldIds.includes(m.id))
      filtered = filtered.slice(Math.max(filtered.length - 20, 0))
      if (filtered.length === 0) {
        return false
      }
      ctx.commit('SET_CHAT_MESSAGES', {
        messages: filtered.concat(ctx.state.messages),
        currentUserId: ctx.getters.userId
      })
      return true
    }

    return false
  },
  async CHAT_LOAD_INITIAL_MESSAGES(ctx: ActionContext<ChatState, RootState>): Promise<void> {
    // Load most recent messages to show on page load
    const { items } = await restChat.getInitialMessages(ctx.getters.partyId)
    if (items.length === 0) {
      return
    }
    ctx.dispatch('CHAT_PROCESS_INCOMING_MESSAGES', { messages: items })
  },
  CHAT_PROCESS_INCOMING_MESSAGES(ctx: ActionContext<ChatState, RootState>, p: { messages: (Message | Poll | Quiz | QuizUserScores)[] }) {
    const messages = p.messages
    const lastMsg = ctx.state.messages.slice(-1)[0]

    for (const message of messages) {
      // deleted message
      if ((message as Message).delete_message_id !== undefined) {
        ctx.commit('DELETE_CHAT_MESSAGE', (message as Message).delete_message_id)
        continue
      }

      // poll
      if ((message as Poll).poll_id !== undefined) {
        ctx.commit('SET_POLL_ID', { pollId: (message as Poll).poll_id, messageId: message.id })
        ctx.commit('ADD_CHAT_POLL', message)
      }

      // quiz
      if (message.type === 'quiz_question') {
        // TODO: Fix 'type' union discriminator, set literal on all message types so casting isn't needed
        ctx.commit('SET_QUIZ_ID', { quizId: (message as Quiz).question_id, messageId: message.id })
        ctx.commit('ADD_CHAT_QUIZ', message)
      }

      if (message.type === 'quiz_user_scores') {
        // Not added to chat
        ctx.commit('UPDATE_USER_SCORES', message)
        continue
      }

      // normal message
      if (lastMsg && (lastMsg.timestamp_ms > message.timestamp_ms || (lastMsg.timestamp_ms === message.timestamp_ms && lastMsg.seq_id >= message.seq_id))) {
        continue
      }

      ctx.commit('ADD_CHAT_MESSAGE', { message, currentUserId: ctx.getters.userId })
    }
  },
  CHAT_ON_INCOMING_MESSAGES(ctx: ActionContext<ChatState, RootState>) {
    EventBus.$on(ON_MESSAGES, (messages: (Message | Poll)[]) => {
      ctx.dispatch('CHAT_PROCESS_INCOMING_MESSAGES', { messages })
    })
  },
  CHAT_ON_INCOMING_POPUP(ctx: ActionContext<ChatState, RootState>) {
    const initTimestamp = Date.now()

    EventBus.$on(ON_POPUP, (popup: Popup) => {
      if (initTimestamp < popup.timestamp_ms) {
        ctx.commit('SET_CHAT_POPUP', popup)
      }
    })
  },
  async CHAT_SEND_MESSAGE(ctx: ActionContext<ChatState, RootState>, params: { sticker: string, text: string }): Promise<void> {
    const replyId = ctx.state.replyId === '' ? null : ctx.state.replyId

    await restChat.sendMessage(ctx.getters.userToken, ctx.getters.partyId, params.text, params.sticker, replyId)
  },
  async CHAT_SEND_MEDIA(ctx: ActionContext<ChatState, RootState>, p: { photo: string, video: string, text: string }): Promise<void> {
    await restChat.sendMedia(ctx.getters.userToken, ctx.getters.partyId, p.text, p.video, p.photo)
  },
  async CHAT_DELETE_MESSAGE(ctx: ActionContext<ChatState, RootState>, params: { messageId: string }): Promise<void> {
    await restChat.deleteMessage(ctx.getters.userToken, ctx.getters.partyId, params.messageId)
  },
  async CHAT_BLOCK_USER(ctx: ActionContext<ChatState, RootState>, params: { userId: number }): Promise<void> {
    await restUser.block(ctx.getters.userToken, ctx.getters.partyId, params.userId)
  },
  async CHAT_UNBLOCK_USER(ctx: ActionContext<ChatState, RootState>, params: { userId: number }): Promise<void> {
    await restUser.unblock(ctx.getters.userToken, ctx.getters.partyId, params.userId)
  },
  async CHAT_UPLOAD_FILE(ctx: ActionContext<ChatState, RootState>, p: { file: File }): Promise<{url: string}> {
    return await restFile.media(ctx.getters.userToken, p.file, ctx.getters.partyId)
  },
  async CHAT_CREATE_POPUP(ctx: ActionContext<ChatState, RootState>, p: { message: object, isCloseable: boolean, isRestart: boolean, isFree: boolean, service: string }): Promise<void> {
    // TODO: Payload should be a PopupMessage object. Don't repeat params here. Applies in other actions as well
    await restChat.sendPopup(ctx.getters.userToken, ctx.getters.partyId, p.message, p.isCloseable, p.isRestart, p.service, p.isFree)
  },
  async CHAT_CREATE_POLL(ctx: ActionContext<ChatState, RootState>, p: { question: string, answers: string[], time: number }): Promise<void> {
    await restChat.postPoll(ctx.getters.userToken, ctx.getters.partyId, p.question, p.answers, p.time)
  },
  async CHAT_VOTE_POLL(ctx: ActionContext<ChatState, RootState>, p: { pollId: string, answerIndex: number }): Promise<void> {
    await restChat.votePoll(ctx.getters.userToken, ctx.getters.partyId, p.pollId, p.answerIndex)
  },
  CHAT_CREATE_QUIZ(ctx: ActionContext<ChatState, RootState>, p: { question: string, answers: string[], correct_answer: number, time: number }) {
    return restChat.postQuiz(ctx.getters.userToken, ctx.getters.partyId, p.question, p.answers, p.correct_answer, p.time)
  },
  async CHAT_VOTE_QUIZ(ctx: ActionContext<ChatState, RootState>, p: { questionId: string, answerIndex: number }) {
    const result = await restChat.voteQuiz(ctx.getters.userToken, ctx.getters.partyId, p.questionId, p.answerIndex)
    ctx.commit('SET_USER_QUIZ_ANSWERS', [ // Replace existing answer for this question in user state
      ...ctx.getters.userQuizAnswers.filter((item: QuizAnswer) => item.question_id !== p.questionId),
      {
        answer_id: p.answerIndex,
        question_id: p.questionId
      }
    ])
    return result
  },
  CHAT_CREATE_QUIZ_SCOREBOARD(ctx: ActionContext<ChatState, RootState>, p: { count: number }) {
    return restChat.postQuizScoreboard(ctx.getters.userToken, ctx.getters.partyId, p.count)
  },
  async CHAT_GET_QUIZ_SCOREBOARD(ctx: ActionContext<ChatState, RootState>, p: { count: number }) {
    return restChat.getQuizScoreboard(ctx.getters.partyId, p.count)
  },
  CHAT_FLUSH(ctx: ActionContext<ChatState, RootState>) {
    return restChat.deleteAll(ctx.getters.partyId)
  },
  async CHAT_LOAD_SCHEDULED_MESSAGES(ctx: ActionContext<ChatState, RootState>): Promise<void> {
    const { items } = await restChat.getScheduled(ctx.getters.partyId)

    ctx.commit('SET_CHAT_SCHEDULED_MESSAGES', items)
  },
  CHAT_CREATE_SCHEDULED_MESSAGE(ctx: ActionContext<ChatState, RootState>, p: { scheduledMessage: ScheduledMessage }) {
    return restChat.createScheduled(ctx.getters.partyId, p.scheduledMessage)
  },
  async CHAT_DELETE_SCHEDULED_MESSAGE(ctx: ActionContext<ChatState, RootState>, p: { scheduledMessageId: number }) {
    await restChat.deleteScheduled(ctx.getters.partyId, p.scheduledMessageId)

    ctx.commit('DELETE_CHAT_SCHEDULED_MESSAGE', p.scheduledMessageId)
  },
  CHAT_FAKE_START(ctx: ActionContext<ChatState, RootState>, p: { messagePerSecond: number, messageStopAfter: number }) {
    return restChat.fakeStart(ctx.getters.partyId, p.messagePerSecond, p.messageStopAfter)
  },
  CHAT_FAKE_STOP(ctx: ActionContext<ChatState, RootState>) {
    return restChat.fakeStop(ctx.getters.partyId)
  },
}

const getters: GetterTree<ChatState, RootState> = {
  chatMessages: (state: ChatState) => {
    if (state.artistFilter) {
      return state.artistMessages
    }

    let start = state.messageIndexLocked > -1 ? (state.messageIndexLocked - state.messagesShownCount) : (state.messages.length - state.messagesShownCount)

    // start can not be negative
    if (start < 0) {
      start = 0
    }

    const end = state.messageIndexLocked > -1 ? (state.messageIndexLocked + 1) : undefined

    return (state.artistFilter ? state.artistMessages : state.messages).slice(start, end)
  },
  chatMessagesShownCount: (state: ChatState) => state.messagesShownCount,
  chatMessagesTotalCount: (state: ChatState) => (
    (state.artistFilter ? state.artistMessages : state.messages).length
  ),
  chatPollIds: (state: ChatState) => state.pollIds,
  chatQuizIds: (state: ChatState) => state.quizIds,
  chatReplyId: (state: ChatState) => state.replyId,
  chatArtistFilter: (state: ChatState) => state.artistFilter,
  chatPopup: (state: ChatState) => state.popup,
  chatReplyMessage: (state: ChatState) => state.messages.find(m => m.id === state.replyId),
  chatScheduledMessages: (state: ChatState) => state.scheduledMessages,
  chatPolls: (state: ChatState) => state.polls,
  chatQuizzes: (state: ChatState) => state.quizzes,
  chatQuizScoresByUserId: (state: ChatState) => state.userScoresById,
  chatTotalQuizQuestions: (state: ChatState) => state.totalQuizQuestions
}

export const chatModule: Module<ChatState, RootState> = {
  state: chatState,
  mutations,
  actions,
  getters,
}
